import { Suspense } from "react";

import { PageSkeleton } from "@stacklet/ui";
import { lazily } from "react-lazily";

import { lazyRouteLoader } from "app/utils/lazyRouteLoader";

import type { PreloadingRouteProps } from "app/utils/lazyRouteLoader";

// Routes.tsx imports this module statically, so it is part of the app shell's
// bundle: the page and its preload module may only be imported dynamically
// here, or the split that lets the loader run ahead of the page's chunk is
// lost.

const { AccountDetailsRoute } = lazily(() => import("./AccountDetailsRoot"));

/**
 * The admin account details route: see AccountDetailsRoot.
 *
 * The read-only mirror of the same page (`/accounts/:accountKey`) stays on
 * lazy load for now — its URL carries no provider, so it would preload a
 * different account than the admin route does for the same account.
 */
export const accountDetailsRoute: PreloadingRouteProps = {
  element: (
    <Suspense fallback={<PageSkeleton />}>
      <AccountDetailsRoute />
    </Suspense>
  ),
  loader: lazyRouteLoader({
    load: () =>
      import("./accountDetailsPreload").then((module) =>
        module.createAccountDetailsLoader(),
      ),
    warm: () => import("./AccountDetailsRoot"),
  }),
  // The details page keeps its own query up to date while it is mounted (see
  // useRoutePreloadedQuery), so the loader only runs again when the URL names
  // a different account — not for the tab and search changes that also write
  // to the URL, which the page and its table handle themselves.
  shouldRevalidate: ({ currentParams, nextParams }) =>
    currentParams.accountProvider !== nextParams.accountProvider ||
    currentParams.accountKey !== nextParams.accountKey,
};
